import type { Owner } from '@/hooks/useOwnersSimple'
import type { OwnerFormData } from '@/schemas/ownerSchema'
import { ownerSchema } from '@/schemas/ownerSchema'
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import InputMask from 'react-input-mask'

interface OwnerFormRHFProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: OwnerFormData) => void
  owner?: Owner | null
  isLoading?: boolean
}

export function OwnerFormRHF({
  isOpen,
  onClose,
  onSubmit,
  owner,
  isLoading = false,
}: OwnerFormRHFProps) {
  const isEditMode = !!owner

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OwnerFormData>({
    resolver: zodResolver(ownerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
  })

  useEffect(() => {
    if (owner) {
      reset({
        name: owner.name || '',
        email: owner.email || '',
        phone: owner.phone || '',
      })
    } else {
      reset({
        name: '',
        email: '',
        phone: '',
      })
    }
  }, [owner, reset])

  const handleFormSubmit = (data: OwnerFormData) => {
    onSubmit(data)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEditMode ? 'Editar' : 'Novo'} Proprietário</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!errors.name}>
                <FormLabel htmlFor="name">Nome Completo</FormLabel>
                <Input
                  id="name"
                  placeholder="Nome do proprietário"
                  {...register('name')}
                />
                <FormErrorMessage>
                  {errors.name && errors.name.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.email} isRequired>
                <FormLabel htmlFor="email">Email</FormLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  {...register('email')}
                  // isDisabled={isEditMode}
                />
                <FormErrorMessage>
                  {errors.email && errors.email.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.phone}>
                <FormLabel htmlFor="phone">Telefone</FormLabel>
                <Input
                  as={InputMask}
                  id="phone"
                  mask="(99) 99999-9999"
                  placeholder="(11) 99999-9999"
                  {...register('phone')}
                />
                <FormErrorMessage>
                  {errors.phone && errors.phone.message}
                </FormErrorMessage>
              </FormControl>

              <HStack justify="flex-end" pt={4} spacing={3}>
                <Button onClick={onClose} variant="ghost">
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={isLoading || isSubmitting}
                  loadingText={isEditMode ? 'Salvando...' : 'Criando...'}
                >
                  {isEditMode ? 'Salvar Alterações' : 'Criar Proprietário'}
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
